import React, { useState } from "react";
import "./ValueFilterStyleSheet.css";
import { Currency } from '../ReferenceDataContext';
import { useCurrency } from '../CurrencyContext';

export type ValuationType = "acquisition" | "insurance" | "fairMarketValue";

export interface ValueRangeFilterState {
    valuationType: ValuationType;
    currency: string;
    min?: number | null;
    max?: number | null;
}

interface ValueRangeFilterProps {
    currencies: Currency[];
    defaultCurrency?: string;
    initial?: ValueRangeFilterState;
    onChange: (value: ValueRangeFilterState | null) => void;
}

const ValueRangeFilter: React.FC<ValueRangeFilterProps> = ({
    currencies,
    defaultCurrency,
    initial,
    onChange,
}) => {

    const { currency } = useCurrency();

    const [valuationType, setValuationType] = useState<ValuationType>(
        initial?.valuationType ?? "acquisition"
    );
    const [selectedCurrency, setCurrency] = useState<string>(
        initial?.currency ?? defaultCurrency ?? currencies[0].currencyCode
    );
    const [min, setMin] = useState<string>(
        initial?.min != null ? initial.min.toString() : ""
    );
    const [max, setMax] = useState<string>(
        initial?.max != null ? initial.max.toString() : ""
    );
    const [filterApplied, setFilterApplied] = useState<boolean>(false);

    const handleApply = () => {
        if (!min && !max && valuationType === "acquisition") {
            // Nothing set – treat as "no value filter"
            onChange(null);
            return;
        }

        const parsedMin = min ? Number(min) : null;
        const parsedMax = max ? Number(max) : null;

        onChange({
            valuationType,
            currency,
            min: isNaN(parsedMin as number) ? null : parsedMin,
            max: isNaN(parsedMax as number) ? null : parsedMax,
        });

        setFilterApplied(true);
    };

    const handleClear = () => {
        setValuationType("acquisition");
        setMin("");
        setMax("");
        onChange(null);
        setFilterApplied(false);
    };

    React.useEffect(() => {

        if (initial?.min == null && initial?.max == null) {
            setFilterApplied(false);
        } else {
            setFilterApplied(true);
        }

    }, [initial]);


    return (
        <div className="value-filter">
            <div className="value-filter__group">
                {filterApplied ? (
                    <div className="value-filter__header">Value Filter Active</div>
                ) : (
                        <div className="value-filter__lowheader">Value Filter Not Set</div>
                )}

                <div className="value-filter__radio-group">
                    <label>
                        <input
                            type="radio"
                            value="acquisition"
                            checked={valuationType === "acquisition"}
                            onChange={() => setValuationType("acquisition")}
                        />
                        <span>Acquisition</span>
                    </label>
                    <label>
                        <input
                            type="radio"
                            value="insurance"
                            checked={valuationType === "insurance"}
                            onChange={() => setValuationType("insurance")}
                        />
                        <span>Insurance</span>
                    </label>
                    <label>
                        <input
                            type="radio"
                            value="fairMarketValue"
                            checked={valuationType === "fairMarketValue"}
                            onChange={() => setValuationType("fairMarketValue")}
                        />
                        <span>Fair Market</span>
                    </label>
                </div>
            </div>

            <div className="value-filter__group value-filter__range">
                <div className="value-filter__label-inline">
                    <span>Range</span>
                    <span className="value-filter__currency-pill">{currency}</span>
                </div>
                <div className="value-filter__range-inputs">
                    <input
                        type="number"
                        inputMode="decimal"
                        placeholder="Min"
                        value={min}
                        onChange={(e) => setMin(e.target.value)}
                    />
                    <span className="value-filter__range-separator">–</span>
                    <input
                        type="number"
                        inputMode="decimal"
                        placeholder="Max"
                        value={max}
                        onChange={(e) => setMax(e.target.value)}
                    />
                </div>
            </div>

            <div className="value-filter__actions">
                <button
                    type="button"
                    className="value-filter__btn value-filter__btn--primary"
                    onClick={handleApply}
                >
                    Set Value Filter
                </button>
                <button
                    type="button"
                    className="value-filter__btn value-filter__btn--ghost"
                    onClick={handleClear}
                >
                    Clear Value Filter
                </button>
            </div>
        </div>
    );
};

export default ValueRangeFilter;
